import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import {
  checkTournamentPermissionUseCase,
  createVictoryPostUseCase,
  deletePostUseCase,
  getBracketLeaderboardUseCase,
  getMatchesByTournamentUseCase,
  getParticipantsByTournamentUseCase,
  getRoundRobinStandingsUseCase,
  getSwissStandingsUseCase,
  getTournamentChampionUseCase,
  getTournamentPostsUseCase,
  likePostUseCase,
  tournamentRepository,
  unlikePostUseCase,
  updateMatchResultUseCase,
  sendNotificationUseCase,
} from '@/src/core/dependencies';
import { generateTournamentPDFHTML } from '@/src/core/utils/pdfTemplates';
import { logger } from '@/src/core/utils';
import { shareFile, triggerDownload } from '@/src/core/utils/platformShare';
import { Participant, PostWithDetails, Tournament } from '@/src/domain/models';
import type { PermissionCheck } from '@/src/domain/usecases/CheckTournamentPermissionUseCase';
import type { LeaderboardEntry } from '@/src/domain/usecases/GetBracketLeaderboardUseCase';
import type { RoundRobinStanding } from '@/src/domain/usecases/GetRoundRobinStandingsUseCase';
import { useTranslation } from '@/src/i18n';
import {
  BracketScreen,
  BracketScreenError,
  BracketScreenLoading,
  MatchWithParticipants,
} from '@/src/presentation/screens/BracketScreen';
import * as MediaLibrary from 'expo-media-library';
import * as Print from 'expo-print';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Share } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { useInterstitialAd } from '@/src/presentation/hooks';
import { captureRef } from 'react-native-view-shot';

type TabKey = 'bracket' | 'leaderboard' | 'posts';

const PAGE_SIZE = 20;

export default function BracketContainer() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<MatchWithParticipants[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [champion, setChampion] = useState<Participant | null>(null);
  const [standings, setStandings] = useState<RoundRobinStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState<MatchWithParticipants | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { checkAccess, limits, isPro } = useSubscription();
  const [permissions, setPermissions] = useState<PermissionCheck>({ hasPermission: false });
  const canUpdateResults = permissions.hasPermission;
  const [activeTab, setActiveTab] = useState<TabKey>('bracket');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const viewRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [posts, setPosts] = useState<PostWithDetails[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsPage, setPostsPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const { showAd } = useInterstitialAd();

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id || !user) return;

    try {
      setLoading(true);
      const [tournamentData, matchesData, participantsData, permissionsData] = await Promise.all([
        tournamentRepository.getById(id),
        getMatchesByTournamentUseCase.execute(id),
        getParticipantsByTournamentUseCase.execute(id),
        checkTournamentPermissionUseCase.execute({
          tournamentId: id,
          userId: user.id,
          permission: 'update_results',
        }),
      ]);

      setTournament(tournamentData);
      setParticipants(participantsData);
      setPermissions(permissionsData);

      if (tournamentData?.status === 'completed') {
        const championData = await getTournamentChampionUseCase.execute(id);
        setChampion(championData);
      } else {
        setChampion(null);
      }

      if (tournamentData?.tournamentType === 'round_robin') {
        const standingsData = await getRoundRobinStandingsUseCase.execute(id);
        setStandings(standingsData);
      }

      if (tournamentData?.tournamentType === 'swiss') {
        const swissStandingsData = await getSwissStandingsUseCase.execute(id);
        // Convert Swiss standings to RoundRobin format for display compatibility
        const standingsData = swissStandingsData.map((s) => ({
          participant: s.participant,
          rank: s.rank,
          played: s.matchesPlayed,
          won: s.wins,
          drawn: s.draws,
          lost: s.losses,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
          points: s.points,
        }));
        setStandings(standingsData);
      }

      const enrichedMatches = matchesData.map((match) => {
        const participant1 = participantsData.find((p) => p.id === match.participant1Id);
        const participant2 = participantsData.find((p) => p.id === match.participant2Id);
        const winner = participantsData.find((p) => p.id === match.winnerId);

        return {
          ...match,
          participant1,
          participant2,
          winner,
        };
      });

      setMatches(enrichedMatches);

      const leaderboardData = await getBracketLeaderboardUseCase.execute(id);
      setLeaderboard(leaderboardData);

      // Load posts for this tournament
      await loadPosts(true);
    } catch (error) {
      logger.error('Bracket', 'Failed to load bracket', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async (reset: boolean = false) => {
    if (!id || !user) return;

    try {
      setPostsLoading(true);
      const offset = reset ? 0 : postsPage * PAGE_SIZE;
      const data = await getTournamentPostsUseCase.execute(id, user.id, PAGE_SIZE, offset);

      if (reset) {
        setPosts(data);
        setPostsPage(0);
      } else {
        setPosts((prev) => [...prev, ...data]);
      }
      setHasMorePosts(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Bracket', 'Failed to load posts', { error });
    } finally {
      setPostsLoading(false);
    }
  };

  const handleLoadMorePosts = async () => {
    if (postsLoading || !hasMorePosts) return;
    setPostsPage((prev) => prev + 1);
    await loadPosts(false);
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;

    platformAlert(t('feed.deletePost'), t('feed.deletePostConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deletePostUseCase.execute(postId, user.id);
            setPosts((prev) => prev.filter((p) => p.id !== postId));
            platformAlert(t('common.success'), t('alerts.postDeleted'));
          } catch (error) {
            logger.error('Bracket', 'Failed to delete post', { error });
            platformAlert(t('common.error'), t('feed.failedToPost'));
          }
        },
      },
    ]);
  };

  const handleLikePress = async (postId: string, isLiked: boolean) => {
    if (!user) return;

    // Optimistic update
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              isLikedByUser: !isLiked,
              likeCount: isLiked ? p.likeCount - 1 : p.likeCount + 1,
            }
          : p
      )
    );

    try {
      if (isLiked) {
        await unlikePostUseCase.execute(postId, user.id);
      } else {
        await likePostUseCase.execute(postId, user.id);
      }
    } catch (error) {
      // Revert optimistic update on error
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                isLikedByUser: isLiked,
                likeCount: isLiked ? p.likeCount + 1 : p.likeCount - 1,
              }
            : p
        )
      );
      logger.error('Bracket', 'Failed to toggle like', { error });
    }
  };

  const handleSharePost = async (postId: string) => {
    const url = `https://tournaly.app/post/${postId}`;
    const title = t('feed.sharePost');
    if (Platform.OS === 'web') {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ url, title }).catch(() => {});
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url).catch(() => {});
        platformAlert(t('common.success'), t('feed.linkCopied'));
      }
    } else {
      await Share.share({ message: url, title }).catch(() => {});
    }
  };

  const handleMatchPress = (match: MatchWithParticipants) => {
    if (!canUpdateResults) {
      platformAlert(t('common.view'), t('alerts.viewOnly'));
      return;
    }

    if (match.status === 'completed') {
      platformAlert(t('tournaments.completed'), t('alerts.matchCompleted'));
      return;
    }

    if (!match.participant1 || !match.participant2) {
      platformAlert(t('common.error'), t('alerts.matchWaiting'));
      return;
    }

    setSelectedMatch(match);
    setScore1('');
    setScore2('');
    setModalVisible(true);
  };

  const handleExportPDF = async () => {
    if (!tournament) {
      platformAlert(t('common.error'), t('errors.failedToLoad'));
      return;
    }

    try {
      setIsExportingPDF(true);

      // Generate HTML content — watermark shown for free users
      const htmlContent = generateTournamentPDFHTML({
        tournament,
        participants,
        standings,
        leaderboard,
        champion,
        showWatermark: !isPro,
      });

      if (Platform.OS === 'web') {
        // On web, open print dialog with the HTML content
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
        } else {
          platformAlert(t('common.error'), t('alerts.popupBlocked'));
        }
      } else {
        // Generate PDF using expo-print — A4 portrait (595 × 842 pt)
        const { uri } = await Print.printToFileAsync({
          html: htmlContent,
          width: 595,
          height: 842,
        });

        // Try to share the PDF
        const shared = await shareFile({
          uri,
          filename: `${tournament.name.replace(/[^a-z0-9]/gi, '_')}.pdf`,
          mimeType: 'application/pdf',
          dialogTitle: t('common.share'),
        });

        if (shared) {
          // Success - no need to show alert, user already interacted with share dialog
        } else {
          platformAlert(t('common.success'), t('alerts.bracketExported'));
        }
      }
    } catch (error) {
      logger.error('Bracket', 'Failed to export PDF', { error });
      platformAlert(t('common.error'), t('alerts.failedToExport'));
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportBracket = async () => {
    if (Platform.OS === 'web') {
      // On web, captureRef doesn't work - show message to use PDF export instead
      platformAlert(
        t('common.info'),
        t('alerts.useExportPDFOnWeb'),
        [{ text: t('common.ok') }]
      );
      return;
    }

    if (!viewRef.current) {
      platformAlert(t('common.error'), t('errors.failedToLoad'));
      return;
    }

    try {
      setIsExporting(true);

      // Wait two frames so React re-renders the watermark before capture
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

      // Capture the view as image (mobile only)
      const uri = await captureRef(viewRef, {
        format: 'png',
        quality: 1,
        result: 'tmpfile',
      });

      const filename = `${tournament?.name?.replace(/[^a-z0-9]/gi, '_') || 'bracket'}.png`;

      // Try to share the image
      const shared = await shareFile({
        uri,
        filename,
        mimeType: 'image/png',
        dialogTitle: t('common.share'),
      });

      if (!shared) {
        // Fallback: Save to media library if sharing is not available
        const { status } = await MediaLibrary.requestPermissionsAsync();
        if (status === 'granted') {
          await MediaLibrary.saveToLibraryAsync(uri);
          platformAlert(t('common.success'), t('alerts.imageSaved'));
        } else {
          platformAlert(t('common.error'), t('alerts.permissionDenied'));
        }
      }
    } catch (error) {
      logger.error('Bracket', 'Failed to export bracket', { error });
      platformAlert(t('common.error'), t('alerts.failedToExport'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSubmitResult = async (winnerId: string | null) => {
    if (!selectedMatch) return;

    const s1 = score1 ? parseInt(score1) : 0;
    const s2 = score2 ? parseInt(score2) : 0;

    let finalWinnerId = winnerId;
    const usesStandings = tournament?.tournamentType === 'round_robin' || tournament?.tournamentType === 'swiss';
    if (usesStandings && !winnerId) {
      if (s1 > s2) {
        finalWinnerId = selectedMatch.participant1Id!;
      } else if (s2 > s1) {
        finalWinnerId = selectedMatch.participant2Id!;
      } else {
        finalWinnerId = selectedMatch.participant1Id!;
      }
    }

    if (!finalWinnerId) {
      platformAlert(t('common.error'), t('alerts.selectWinner'));
      return;
    }

    const isGrandFinal = selectedMatch.bracketType === 'grand_final' && selectedMatch.round === 1;

    try {
      setSubmitting(true);
      await updateMatchResultUseCase.execute({
        matchId: selectedMatch.id,
        winnerId: finalWinnerId,
        scoreParticipant1: s1,
        scoreParticipant2: s2,
      });

      setModalVisible(false);
      setSelectedMatch(null);

      const previousStatus = tournament?.status;
      await loadData();

      // Notify match participants about the result
      try {
        const p1Id = selectedMatch.participant1Id;
        const p2Id = selectedMatch.participant2Id;
        const matchParticipants = participants.filter(
          (p) => p.userId && p.userId !== user?.id && (p.id === p1Id || p.id === p2Id)
        );
        for (const p of matchParticipants) {
          await sendNotificationUseCase.execute(
            p.userId!,
            'match_result',
            t('notifications.matchResult'),
            t('notifications.matchResultBody', { name: tournament?.name || '' }),
            { tournamentId: id! }
          );
        }
      } catch {}

      // Try to create a victory post if tournament just completed
      // The use case will only create a post if the tournament is completed
      try {
        await createVictoryPostUseCase.execute(
          id!,
          t('feed.victoryPost', { tournamentName: tournament?.name || '' })
        );
      } catch (error) {
        logger.error('Bracket', 'Failed to create victory post', { error });
        // Don't show error to user - victory post is a nice-to-have
      }

      // Check if tournament just completed and notify all participants + show ad
      const updatedTournament = await tournamentRepository.getById(id!);
      if (updatedTournament?.status === 'completed' && previousStatus !== 'completed') {
        showAd();
        try {
          for (const p of participants) {
            if (p.userId && p.userId !== user?.id) {
              await sendNotificationUseCase.execute(
                p.userId,
                'tournament_completed',
                t('notifications.tournamentCompleted'),
                t('notifications.tournamentCompletedBody', { name: tournament?.name || '' }),
                { tournamentId: id! }
              );
            }
          }
        } catch {}
      }

      if (isGrandFinal) {
        const updatedMatches = await getMatchesByTournamentUseCase.execute(id!);
        const grandFinalReset = updatedMatches.find((m) => m.bracketType === 'grand_final' && m.round === 2);

        if (grandFinalReset) {
          platformAlert(
            t('tournaments.bracket'),
            t('alerts.bracketGenerated'),
            [{ text: t('common.ok') }]
          );
        } else {
          platformAlert(t('alerts.tournamentComplete'), t('alerts.weHaveChampion'), [{ text: t('common.ok') }]);
        }
      } else {
        platformAlert(t('common.success'), t('alerts.matchResultUpdated'));
      }
    } catch (error: any) {
      logger.error('Bracket', 'Failed to update match', { error });
      platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <BracketScreenLoading colors={colors} />;
  }

  if (!tournament) {
    return <BracketScreenError colors={colors} message={t('tournaments.tournamentNotFound')} />;
  }

  return (
    <>
      <Stack.Screen options={{ title: `${tournament.name} - ${t('tournaments.bracket')}` }} />
      <BracketScreen
        tournament={tournament}
        matches={matches}
        participants={participants}
        champion={champion}
        standings={standings}
        leaderboard={leaderboard}
        posts={posts}
        postsLoading={postsLoading}
        currentUserId={user?.id}
        onUserPress={handleUserPress}
        onDeletePost={handleDeletePost}
        onLikePress={handleLikePress}
        onSharePost={handleSharePost}
        onLoadMorePosts={handleLoadMorePosts}
        canUpdateResults={canUpdateResults}
        userRole={permissions.userRole}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        selectedMatch={selectedMatch}
        score1={score1}
        setScore1={setScore1}
        score2={score2}
        setScore2={setScore2}
        submitting={submitting}
        handleMatchPress={handleMatchPress}
        handleSubmitResult={handleSubmitResult}
        handleExportBracket={handleExportBracket}
        handleExportPDF={handleExportPDF}
        isExporting={isExporting}
        isExportingPDF={isExportingPDF}
        viewRef={viewRef}
        isPro={isPro}
        colors={colors}
      />
    </>
  );
}
